import React, { useEffect, useMemo, useRef, useState } from "react";
import { convertHTMLRawStringToHTML, RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCPrintContainer, RCCServicesApi, RCCSwitch, RCCTooltip } from "react-common-components";
import { useParams } from "react-router-dom";
import Loader from "src/components/Loader";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { useAuth } from "src/context/Auth/context";
import formatDate from "src/utils/formatDate";
import { ActionSituationFilter } from "src/pages/Actions/Panel/interface";
import Pagination from "src/components/Pagination";
import Title from "src/components/Title";
import { IRefPrintContainer } from "react-common-components/dist/components/PrintContainer/interface";
import BlockContainer, { BlueSpan, paginationsStlyes, PrintStyles } from "./style";
import { FormMeetingAction, IMeetingFormDTO } from "../../interface";

export interface Itens{
    item1: string;
    item2: string;
}

const MeetingPrint: React.FC = () => {
	const params = useParams<{
        meetingId: string;
    }>();

	const { translate,currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { company } = useAuth();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
    
	const [loading, setLoading] = useState(false);
	const [errorLoading, setErrorLoading] = useState(false);
	const [meetingSelected, setMeetingSelected] = useState<IMeetingFormDTO>({} as IMeetingFormDTO);

	const [showParticipants, setShowParticipants] = useState(false);
	const [showActions, setShowActions] = useState(false);
	const [showAgenda, setShowAgenda] = useState(false);

	// Print
	const printRef = useRef<IRefPrintContainer>(null);
	const executePrint = () => {
		printRef.current?.print();
	};

	async function getMeeting(idMeeting: number){
		setLoading(true);
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Meetings/Select",
				urlParameters: {id: idMeeting,mode:FormMeetingAction.VIEW}
			});

			setMeetingSelected(data);
			setLoading(false);

		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar detalhes da reunião"), addToast);
			setLoading(false);
			setErrorLoading(true);
		}
	}

	const colActionSituation = (row: any) => {
		let text;
		switch(	row.situation){
		case ActionSituationFilter.FINISHED:
			text = translate("Concluída");
			break;
		case ActionSituationFilter.CANCELED:
			text = translate("Cancelada");
			break;
		case ActionSituationFilter.IN_PROGRESS:
			text = translate("Em andamento");
			break;
		default: 
			text = translate("Não iniciada");
			break;
		}
		return (
			<>
				<span>
					{text}
				</span>
			</>
		);
	};

	const renderInfoMeeting = useMemo(() => (
		<>
			<RCCPrintContainer
				ref={printRef}
				styleInline={PrintStyles}
				documentTitle={`${translate("Reunião")}-${meetingSelected.ara001Assunto}`}
			>
				<RCCGrid 
					container
					justifyContent="center"
					className="noPrintElement"
					styleInline="padding: 10px"
				>
					<BlueSpan><h2>{meetingSelected.ara001Assunto}</h2></BlueSpan>   
					<hr/>
				</RCCGrid>
				<RCCGrid container item className="onlyPrintElement" wrap="nowrap" justifyContent="space-evenly">
					{company.efa002Imagem && (
						<img src={`${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_empresas/${company.efa002Imagem}`} alt="Company Logo" />
					)}
					<Title color="black" size="xl" bold >
						{`${translate("Reunião")} : ${meetingSelected.ara001Assunto}`}
					</Title>
				</RCCGrid>
				<RCCGrid container>
					<RCCGrid container>
						<BlueSpan><h3>{translate("Dados Gerais")}</h3></BlueSpan>
					</RCCGrid>
					<BlockContainer>
						<RCCGrid container>
							<strong>{`${translate("Assunto")}:`}</strong>&nbsp;
							<span>{ meetingSelected.ara001Assunto}</span>
						</RCCGrid>
						<RCCGrid container>
							<strong>{translate("Data")}:</strong>&nbsp;
							<span>{`${formatDate(meetingSelected.ara001Dthrinicio,currentLanguage,true)} ${translate("até")} ${formatDate(meetingSelected.ara001Dthrfim,currentLanguage,true,"dd/MM/yyyy")}`}</span>
						</RCCGrid>
						<RCCGrid container>
							<strong>{translate("Responsável")}:</strong>&nbsp;
							<span>{meetingSelected.ara001Efa005Nome}</span>
						</RCCGrid>
						<RCCGrid container>
							<strong>{translate("Sala")}:</strong>&nbsp;
							<span>{meetingSelected.taa008Descricao}</span>
						</RCCGrid>
						{meetingSelected.ara001Taa010Id && 
							<RCCGrid container>
								<strong>{translate("Tipo")}:</strong>&nbsp;
								<span>{meetingSelected.taa010Descricao}</span>
							</RCCGrid>
						}
						{meetingSelected.ara001Mea001Id && 
							<RCCGrid container>
								<strong>{translate("Mapa")}:</strong>&nbsp;
								<span>{meetingSelected.mea001Nome}</span>
							</RCCGrid>
						}
					</BlockContainer>
				</RCCGrid>	
				{showAgenda &&
					<RCCGrid container>
						<RCCGrid container>
							<BlueSpan><h3>{translate("Pauta")}</h3></BlueSpan>
						</RCCGrid>
						<BlockContainer>
							<RCCGrid item dangerouslySetInnerHTML={{__html: `<p>${convertHTMLRawStringToHTML(meetingSelected.ara001Pauta).html}</p>` }} />
						</BlockContainer>
					</RCCGrid>
				}

				{meetingSelected.ara001Ata &&
					<RCCGrid container>
						<RCCGrid container>
							<BlueSpan><h3>{translate("Ata")}</h3></BlueSpan>
						</RCCGrid>
						<BlockContainer>
							<RCCGrid item dangerouslySetInnerHTML={{__html: convertHTMLRawStringToHTML(meetingSelected.ara001Ata).html }} />
						</BlockContainer>
					</RCCGrid>
				}

				{showParticipants && meetingSelected.participants && meetingSelected.participants.length>0 &&
					<RCCGrid container>
						<RCCGrid container>
							<BlueSpan><h3>{translate("Participantes")}</h3></BlueSpan>
						</RCCGrid>
						<BlockContainer>
							<Pagination 
								showHeader
								renderType="infiniteScroll"
								styleInline={paginationsStlyes}
								dataOrRoute={meetingSelected.participants}
								columns={[{
									idColumn: "ara002Efa005Id",
									nameColumn: translate("Nome"),
									xs: 3,
									justify: "left",
									styleInline:"padding-left:5px !important;",
									truncate: true,
									cellRenderer: (row:any) => <span>{row.ara002Efa005Id ? row.efa005Nome:row.ara002Participante}</span>
								},{
									idColumn: "ara002Presente",
									nameColumn: translate("Presente"),
									xs: 2,
									justify: "left",
									truncate: true,
									cellRenderer: (row:any) => <span>{row.ara002Presente === "S" ? translate("Sim"):translate("Não")}</span>
								},{
									idColumn: "ara002Acordoata",
									nameColumn: translate("Concorda"),
									xs: 2,
									justify: "left",
									truncate: true,
									cellRenderer: (row:any) => <span>{row.ara002Acordoata === "S"  ? translate("De acordo"):translate("Em desacordo")}</span>
								},{
									idColumn: "",
									nameColumn: translate(""),
									xs: 5,
									justify: "left",
									cellRenderer:(row:any) =>
										<>
											{ row.ara002Comentario &&
											<BlockContainer style={{padding:"5px"}}>
												<p style={{textAlign:"justify"}}><strong>{translate("Comentário")}:</strong>&nbsp;{row.ara002Comentario}</p>
											</BlockContainer>
											}
										</> 
								}]}
							/>
						</BlockContainer>
					</RCCGrid>
				}
			
				{showActions && meetingSelected.actions && meetingSelected.actions.length > 0 &&
					<RCCGrid container>
						<RCCGrid container>
							<BlueSpan><h3>{translate("Ações")}</h3></BlueSpan>
						</RCCGrid>
						<BlockContainer>
							<Pagination 
								showHeader
								dataOrRoute={meetingSelected.actions}
								renderType="infiniteScroll"
								styleInline={paginationsStlyes}
								columns={[{
									idColumn: "inb001Titulo",
									nameColumn: translate("Nome"),
									xs: 3,
									justify: "left",
									styleInline:"padding-left:5px !important;",
									truncate: true
								},{
									idColumn: "inb001Efa005Nome",
									nameColumn: translate("Responsável"),
									xs: 2,
									justify: "left",
									truncate: true
								},{
									idColumn: "situation",
									nameColumn: translate("Situação"),
									xs: 2,
									justify: "left",
									cellRenderer:colActionSituation
								},{
									idColumn: "inb001Dtinicio",
									nameColumn: translate("Previsão"),
									xs: 3,
									justify: "left",
									cellRenderer:(row:any) => <span>{`${formatDate(row.inb001Dtinicio,currentLanguage)} - ${formatDate(row.inb001Dtfim,currentLanguage)}`}</span>
								}]}
							/>
						</BlockContainer>
					</RCCGrid>
				}
			</RCCPrintContainer>
		</>
	),[meetingSelected, showParticipants, showActions, showAgenda]);

	useEffect(() => {
		getMeeting(Number(params.meetingId));
	}, [params]);

	return (
		<>
			{loading ? (
				<Loader/>) : (
				<>
					{
						errorLoading ? (
							<RCCAlert 
								title={translate("Atenção")}
								description={translate("Erro ao informações")}
							/>
						) : (
							<>
								<RCCGrid 
									className="print"
									container
									justifyContent="space-between"
									alignContent="center"
								>
									<RCCGrid></RCCGrid>
									<RCCGrid
										container
										justifyContent="space-between" 
										styleInline="width: unset !important; display: flex"
									>
										<RCCSwitch styleInline="margin: 10px 10px"
											name="participants" 
											label={translate("Visualizar Participantes")} 
											defaultChecked={showParticipants}
											onChange={() => {setShowParticipants((prevState) => !prevState);}}
										/>
										<RCCSwitch styleInline="margin: 10px 10px"
											name="actions" 
											label={translate("Visualizar Ações")} 
											defaultChecked={showActions}
											onChange={() => {setShowActions((prevState) => !prevState);}}
										/>
										<RCCSwitch styleInline="margin: 10px 10px"
											name="agenda" 
											label={translate("Visualizar Pauta")} 
											defaultChecked={showAgenda}
											onChange={() => {setShowAgenda((prevState) => !prevState);}}
										/>
									</RCCGrid>
									<RCCGrid>
										<RCCTooltip
											text={translate("Imprimir")}
											color="blue"
										>
											<RCCButton
												icon={{
													name: "print",
													color: "#FFF",
													size: 16}
												}
												color="blue"
												size="md"
												onClick={() => executePrint()}
											/>
										</RCCTooltip>
									</RCCGrid>
								</RCCGrid>
								<hr />

								{renderInfoMeeting}
							</>
						)
					}
				</>
			)
			}
		</>
	);
};

export default MeetingPrint;
